import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { AddressBookWalletService } from "@modules/main/components/address-book-wallet/services/address-book-wallet.service";
import {
	AddressBookWalletHostExtra,
	AddressBookWalletHostModel,
	AddressBookWalletHostType,
	AddressBookWalletPayload,
	AddressBookWalletTransactionData
} from "@modules/main/components/address-book-wallet/model/address-book-wallet.model";
import { EMPTY, Observable, Subscription, take } from "rxjs";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { containsString } from "@helpers/utils";
import { InfoService } from "@core-services/info.service";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { CustomModal } from "@modules/core/components/base-modals";
import { map } from "rxjs/operators";
import { AddressBookHoldModalFacade } from "../services/address-book-hold-modal.facade";

@Component({
	selector: "app-address-book-hold-modal",
	templateUrl: "./address-book-hold-modal.component.html",
	styleUrl: "./address-book-hold-modal.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AddressBookHoldModalComponent extends CustomModal implements OnInit, OnDestroy {
	@Input() data: Data;
	public transactionData: AddressBookWalletTransactionData;
	public walletHosts$: Observable<BpSelectOption[]> = EMPTY;
	public walletOwnerError: string;
	public hostHintError: string;
	public hasWalletHost: boolean = false;

	public newWalletForm = this.formBuilder.group({
		walletOwner: ["", Validators.required],
		walletHost: ["", Validators.required],
		otherHostName: "",
		ownWallet: false,
		selfDeclared: false
	});

	private profileName: string = "";
	private transactionId: string = "";
	private addressBookId: string = "";
	private selectedWalletHostId: string;
	private filterHostsSubscription: Subscription = Subscription.EMPTY;

	protected readonly addressBookHoldModalFacade = inject(AddressBookHoldModalFacade);

	constructor(
		private readonly walletService: AddressBookWalletService,
		private readonly profileFacade: NewProfileFacade,
		private readonly infoService: InfoService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly universalModalService: UniversalModalService,
		private readonly cdr: ChangeDetectorRef,
		private formBuilder: FormBuilder
	) {
		super();
	}

	ngOnInit(): void {
		this.initForm();
		this.walletHosts$ = this.addressBookHoldModalFacade.getWalletHostsOptions().pipe(
			map((hosts: BpSelectOption<AddressBookWalletHostModel>[]) => [
				{
					name: this.translationService.translate("self_hosted_wallet"),
					value: AddressBookWalletHostExtra.SELF
				},
				...hosts,
				{
					name: this.translationService.translate("other_host"),
					value: AddressBookWalletHostExtra.OTHER
				}
			])
		);
	}

	private initForm(): void {
		this.transactionId = this.route.snapshot.queryParams["transaction_id"] || this?.data?.transactionId;
		this.addressBookId = this.route.snapshot.queryParams["address_book_id"] || this?.data?.addressBookId;
		if (this.transactionId && this.addressBookId) {
			this.walletService.getPaymentTransaction$(this.transactionId).subscribe({
				next: (transaction: AddressBookWalletTransactionData) => {
					this.transactionData = transaction;
					this.hasWalletHost = Boolean(transaction?.payment_data?.wallet_host);
					this.cdr.detectChanges();
				},
				error: () => {
					this.infoService.setError(this.translationService.translate("generic_error_title"));
					this.cancel();
				}
			});
		} else {
			this.cancel();
		}
	}

	public getHostSearch(): Function {
		return (searchInput: string, { name }: BpSelectOption): boolean => containsString(name, searchInput);
	}

	public onWalletHostChange(selectedWalletHost: string): void {
		this.filterHostsSubscription = this.walletHosts$.subscribe({
			next: (hosts) => {
				let updatedWalletHostId = null;
				for (const host of hosts) {
					if (host.name === selectedWalletHost) {
						updatedWalletHostId = host?.["wallet_host_id"];
						break;
					}
				}
				this.selectedWalletHostId = updatedWalletHostId;
			}
		});
		this.newWalletForm.controls.walletHost.setValue(selectedWalletHost);
	}

	public onOwnWalletChange(isOwnWallet: boolean): void {
		this.profileFacade
			.getProfile$()
			.pipe(take(1))
			.subscribe(({ fullName }: ProfileModel) => (this.profileName = fullName));
		this.newWalletForm.controls.ownWallet.setValue(isOwnWallet);
		if (isOwnWallet) {
			this.newWalletForm.controls.walletOwner.disable();
			this.newWalletForm.controls.walletOwner.setValue(this.profileName);
		} else {
			this.newWalletForm.controls.walletOwner.enable();
			this.newWalletForm.controls.walletOwner.reset();
		}
	}

	public back(): void {
		this.cancel();
	}

	public cancel(): void {
		this.modalService.popAll();
		this.universalModalService.close(ModalCloseReason.close);
		if (!this.data?.transactionId && !this.data?.addressBookId) {
			this.router.navigateByUrl("/home");
		}
	}

	private handleInlineError(errorCode: string, field: string): void {
		const errorMapper: Map<string, string> = new Map([
			["validation.exception.wallet_address.characters", "address_book_validation_address_invalid_characters"],
			["validation.exception.wallet_address.length", "address_book_validation_address_too_many"],
			["validation.exception.wallet_address.address_invalid", "address_book_validation_address_invalid"],
			["validation.exception.wallet_address.not_unique", "address_book_validation_address_already_saved"],
			["validation.exception.wallet_host_hint.length", "address_book_validation_hint_too_many"],
			["validation.exception.owner_legal_name.length", "address_book_validation_owner_name_too_many"],
			["validation.exception.wallet_destination_tag.invalid", "address_book_validation_destination_tag_invalid"]
		]);

		if (field === "wallet_host_hint") {
			this.newWalletForm.controls.otherHostName.setErrors({ invalid_host_hint: true });
			this.hostHintError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_hint_generic");
		}

		if (field === "owner_legal_name") {
			this.newWalletForm.controls.walletOwner.setErrors({ invalid_owner_name: true });
			this.walletOwnerError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_owner_name_generic");
		}
	}

	public onSelfDeclaredChange(selfDeclared: boolean): void {
		this.newWalletForm.controls.selfDeclared.setValue(selfDeclared);
	}

	public updateWallet(): void {
		const editWalletPayload: AddressBookWalletPayload = {
			coin_network_id: this.transactionData?.payment_data?.coin_network_id,
			wallet_address: this.transactionData?.payment_data?.wallet_address,
			user_owned: this.newWalletForm.controls.ownWallet.value,
			self_declared: this.newWalletForm.controls.selfDeclared.value,
			wallet_host_id: this.selectedWalletHostId,
			...(this.newWalletForm.controls.walletHost.value !== AddressBookWalletHostExtra.SELF
				? {
						wallet_host_hint:
							this.newWalletForm.controls.walletHost.value === AddressBookWalletHostExtra.OTHER
								? this.newWalletForm.controls.otherHostName.value
								: null,
						wallet_host_type: AddressBookWalletHostType.VASP
					}
				: {
						wallet_host_hint: null,
						wallet_host_type: AddressBookWalletHostType.SELF_HOSTED
					}),
			...(!this.newWalletForm.controls.ownWallet.value && {
				owner_legal_name: this.newWalletForm.controls.walletOwner.value
			})
		};

		this.walletService
			.editLegacyWallet(this.addressBookId, editWalletPayload)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.infoService.setSuccess(
						this.translationService.translate("address_book_confirmation_of_verification"),
						false,
						5000,
						this.translationService.translate("address_book_processing_information")
					);
					this.cancel();
				},
				error: (err) => {
					const { title, code, field } = err.responseErrors[0];
					const errorMessage: string = title;
					this.infoService.setError(errorMessage);
					this.handleInlineError(code, field);
				}
			});
	}

	ngOnDestroy(): void {
		this.filterHostsSubscription.unsubscribe();
	}
}
